import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import axiosInstance from '../api';
import './TrustCenterPage.css';

function ScoreGauge({ score }) {
  const color = score >= 80 ? '#16a34a' : score >= 60 ? '#d97706' : '#dc2626';
  const label = score >= 80
    ? i18n.t('TrustCenterPage.scoreLabelAuditReady')
    : score >= 60
      ? i18n.t('TrustCenterPage.scoreLabelOnTrack')
      : i18n.t('TrustCenterPage.scoreLabelNeedsWork');
  const circumference = 2 * Math.PI * 54;
  const dash = circumference * (score / 100);

  return (
    <div className="tcp-gauge-wrap">
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r="54" fill="none" stroke="#e2e8f0" strokeWidth="12" />
        <circle
          cx="65" cy="65" r="54" fill="none"
          stroke={color} strokeWidth="12"
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeDashoffset={circumference * 0.25}
          strokeLinecap="round"
        />
      </svg>
      <div className="tcp-gauge-inner">
        <div className="tcp-gauge-score" style={{ color }}>{score}%</div>
        <div className="tcp-gauge-label">{label}</div>
      </div>
    </div>
  );
}

function TrustCenterPage() {
  const { t } = useTranslation();
  const { slug }   = useParams();
  const [data,     setData]    = useState(null);
  const [score,    setScore]   = useState(null);
  const [loading,  setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [docs,     setDocs]    = useState([]);
  const [reqDoc,   setReqDoc]  = useState(null);            // document being requested (opens modal)
  const [reqForm,  setReqForm] = useState({ firstName: '', lastName: '', email: '', company: '', reason: '' });
  const [reqState, setReqState] = useState('idle');        // idle | sending | done | error
  const [tab,      setTab]     = useState('overview');     // overview | controls | testing | subprocessors | documents
  const [stReq,    setStReq]   = useState(null);            // security-report request modal ({reportType}) | null
  const [stForm,   setStForm]  = useState({ email: '', company: '' });
  const [stState,  setStState] = useState('idle');         // idle | sending | done | error

  useEffect(() => {
    axiosInstance.get(`/trust-center/public/${slug}/documents`)
      .then(r => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDocs([]));
    axiosInstance.get(`/trust-center/public/${slug}`)
      .then(res => {
        setData(res.data);
        if (res.data.showReadinessScore) {
          // Use the canonical readiness score from the PUBLIC response — the SAME composite the org
          // sees on its dashboard, so the two never disagree (this widget is labelled "Readiness
          // Score"; raw control coverage is a separate stat on the Security Controls tab). Falls back
          // to coverage for older payloads. The old call to /audit/readiness-score was authenticated
          // → 401 for anonymous visitors → the global interceptor bounced them to login.
          setScore(res.data.readinessScore ?? res.data.controlSummary?.coverage ?? null);
        }
      })
      .catch(err => {
        if (err.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="tcp-page">
      <div className="tcp-spinner-wrap"><div className="tcp-spinner" /></div>
    </div>
  );

  if (notFound) return (
    <div className="tcp-page">
      <div className="tcp-not-found">
        <i className="fas fa-shield-alt tcp-nf-icon" />
        <h2>{t('TrustCenterPage.notFoundTitle')}</h2>
        <p>{t('TrustCenterPage.notFoundBody')}</p>
      </div>
    </div>
  );

  const certList = (data.certifications || '')
    .split(',').map(s => s.trim()).filter(Boolean);

  // Group documents by category for display.
  const docsByCategory = docs.reduce((acc, d) => {
    const c = d.category || t('TrustCenterPage.docsUncategorized');
    (acc[c] = acc[c] || []).push(d);
    return acc;
  }, {});

  const openRequest = (doc) => {
    setReqDoc(doc);
    setReqState('idle');
    setReqForm({ firstName: '', lastName: '', email: '', company: '', reason: '' });
  };

  const submitRequest = (e) => {
    e.preventDefault();
    if (!reqForm.email) return;
    setReqState('sending');
    axiosInstance.post(`/trust-center/public/${slug}/documents/${reqDoc.id}/request-access`, reqForm)
      .then(() => setReqState('done'))
      .catch(() => setReqState('error'));
  };

  const frameworks  = Array.isArray(data.frameworks) ? data.frameworks : [];
  const hasControls = Array.isArray(data.controlCategories) && data.controlCategories.length > 0;
  const hasSubs     = Array.isArray(data.subprocessors) && data.subprocessors.length > 0;
  const hasDocs     = docs.length > 0;
  const sec         = data.securityTesting || null;
  const secProgs    = sec ? ['asm', 'iasm', 'bas'].map(k => sec[k]).filter(p => p && p.enabled) : [];
  const hasTesting  = secProgs.length > 0;

  const submitReport = (e) => {
    e.preventDefault();
    if (!stForm.email) return;
    setStState('sending');
    axiosInstance.post(`/trust-center/public/${slug}/security-report/request`, stForm)
      .then(() => setStState('done'))
      .catch(() => setStState('error'));
  };
  const fwStatusLabel = (s) => s === 'audit-ready'
    ? t('TrustCenterPage.fwAuditReady')
    : s === 'in-progress' ? t('TrustCenterPage.fwInProgress') : t('TrustCenterPage.fwTracked');

  return (
    <div
      className="tcp-page"
      style={{
        '--tcp-primary': data.primaryColor || '#1e3a5f',
        '--tcp-accent':  data.accentColor  || '#1d4ed8',
      }}
    >
      {/* Hero */}
      <div className="tcp-hero">
        <div className="tcp-hero-inner">
          <div className="tcp-logo">
            {data.logoUrl
              ? <img src={data.logoUrl} alt={data.orgName} className="tcp-logo-img" />
              : <i className="fas fa-shield-alt" />}
          </div>
          <h1 className="tcp-org-name">{data.orgName}</h1>
          <p className="tcp-hero-sub">{t('TrustCenterPage.heroSubtitle')}</p>
          {data.customMessage && (
            <p className="tcp-custom-msg">{data.customMessage}</p>
          )}
        </div>
      </div>

      <div className="tcp-content">
        {/* Tab navigation */}
        <div className="tcp-tabs">
          <button type="button" className={`tcp-tab ${tab === 'overview' ? 'tcp-tab-active' : ''}`} onClick={() => setTab('overview')}>{t('TrustCenterPage.tabOverview')}</button>
          {hasControls && <button type="button" className={`tcp-tab ${tab === 'controls' ? 'tcp-tab-active' : ''}`} onClick={() => setTab('controls')}>{t('TrustCenterPage.securityControlsTitle')}</button>}
          {hasTesting && <button type="button" className={`tcp-tab ${tab === 'testing' ? 'tcp-tab-active' : ''}`} onClick={() => setTab('testing')}>{t('TrustCenterPage.testingTitle', 'Security Testing')}</button>}
          {hasSubs && <button type="button" className={`tcp-tab ${tab === 'subprocessors' ? 'tcp-tab-active' : ''}`} onClick={() => setTab('subprocessors')}>{t('TrustCenterPage.subprocessorsTitle')}</button>}
          {hasDocs && <button type="button" className={`tcp-tab ${tab === 'documents' ? 'tcp-tab-active' : ''}`} onClick={() => setTab('documents')}>{t('TrustCenterPage.documentsTitle')}</button>}
        </div>

        {tab === 'overview' && (<>
        {/* System Description (SOC 2 Section III) — the org's overview heading. Server-sanitized HTML. */}
        {data.systemDescription && (
          <div className="tcp-card">
            <h3 className="tcp-card-title">{t('TrustCenterPage.systemDescriptionTitle', { org: data.orgName })}</h3>
            <div className="tcp-sysdesc" dangerouslySetInnerHTML={{ __html: data.systemDescription }} />
          </div>
        )}

        {/* Compliance / framework badges */}
        {frameworks.length > 0 && (
          <div className="tcp-card">
            <h3 className="tcp-card-title">{t('TrustCenterPage.complianceTitle')}</h3>
            <div className="tcp-fw-badges">
              {frameworks.map((f, i) => (
                <div key={i} className={`tcp-fw-badge tcp-fw-${f.status}`}>
                  <i className="fas fa-certificate" />
                  <div className="tcp-fw-text">
                    <span className="tcp-fw-name">{f.name}</span>
                    <span className="tcp-fw-status">{fwStatusLabel(f.status)} · {f.coverage}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Readiness Score */}
        {data.showReadinessScore && score !== null && (
          <div className="tcp-card tcp-score-card">
            <h3 className="tcp-card-title">{t('TrustCenterPage.readinessScoreTitle')}</h3>
            <ScoreGauge score={score} />
            <p className="tcp-score-note">
              {t('TrustCenterPage.readinessScoreNote')}
            </p>
          </div>
        )}

        {/* Certifications */}
        {certList.length > 0 && (
          <div className="tcp-card">
            <h3 className="tcp-card-title">{t('TrustCenterPage.certificationsTitle')}</h3>
            <div className="tcp-cert-list">
              {certList.map((cert, i) => (
                <div key={i} className="tcp-cert-item">
                  <i className="fas fa-certificate tcp-cert-icon" />
                  <span>{cert}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* digests linking to the deep tabs */}
        <div className="tcp-digests">
          {hasControls && (
            <button type="button" className="tcp-digest" onClick={() => setTab('controls')}>
              <span className="tcp-digest-num">{(data.controlSummary && data.controlSummary.coverage) || 0}%</span>
              <span className="tcp-digest-label">{t('TrustCenterPage.securityControlsTitle')}</span>
              <span className="tcp-digest-link">{t('TrustCenterPage.viewAll')} →</span>
            </button>
          )}
          {hasTesting && (
            <button type="button" className="tcp-digest" onClick={() => setTab('testing')}>
              <span className="tcp-digest-num"><i className="fas fa-satellite-dish" /></span>
              <span className="tcp-digest-label">{t('TrustCenterPage.testingTitle', 'Security Testing')}</span>
              <span className="tcp-digest-link">{t('TrustCenterPage.testingDigest', 'Continuously tested')} →</span>
            </button>
          )}
          {hasSubs && (
            <button type="button" className="tcp-digest" onClick={() => setTab('subprocessors')}>
              <span className="tcp-digest-num">{data.subprocessors.length}</span>
              <span className="tcp-digest-label">{t('TrustCenterPage.subprocessorsTitle')}</span>
              <span className="tcp-digest-link">{t('TrustCenterPage.viewAll')} →</span>
            </button>
          )}
          {hasDocs && (
            <button type="button" className="tcp-digest" onClick={() => setTab('documents')}>
              <span className="tcp-digest-num">{docs.length}</span>
              <span className="tcp-digest-label">{t('TrustCenterPage.documentsTitle')}</span>
              <span className="tcp-digest-link">{t('TrustCenterPage.viewAll')} →</span>
            </button>
          )}
        </div>
        </>)}

        {/* ── CONTROLS tab ── */}
        {tab === 'controls' && hasControls && (
          <div className="tcp-card">
            <h3 className="tcp-card-title">{t('TrustCenterPage.securityControlsTitle')}</h3>
            {data.controlSummary && data.controlSummary.totalControls > 0 && (
              <p className="tcp-controls-summary">
                {t('TrustCenterPage.controlsSummary', {
                  passing: data.controlSummary.passing,
                  total: data.controlSummary.totalControls,
                  coverage: data.controlSummary.coverage,
                })}
              </p>
            )}
            <div className="tcp-controls-grid">
              {data.controlCategories.map((c, i) => {
                const cov = Number(c.coverage) || 0;
                const tone = cov >= 80 ? 'tcp-ctrl-pass' : cov >= 60 ? 'tcp-ctrl-partial' : 'tcp-ctrl-low';
                return (
                  <div key={c.shorthand || i} className={`tcp-ctrl-item ${tone}`}>
                    <div className="tcp-ctrl-head">
                      <span className="tcp-ctrl-cat">{c.shorthand}</span>
                      <span className="tcp-ctrl-cov">{c.passed}/{c.total} · {cov}%</span>
                    </div>
                    <span className="tcp-ctrl-label">{c.name}</span>
                    <div className="tcp-ctrl-bar">
                      <div className="tcp-ctrl-bar-fill" style={{ width: `${cov}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── SECURITY TESTING tab ── */}
        {tab === 'testing' && hasTesting && (
          <div className="tcp-card">
            <h3 className="tcp-card-title">{t('TrustCenterPage.testingTitle', 'Continuous Security Testing')}</h3>
            <p className="tcp-controls-summary">
              {t('TrustCenterPage.testingIntro',
                 'We run independent security testing continuously against our own environment — each programme below was last assessed on the date shown, not once a year.')}
            </p>
            <div className="tcp-st-grid">
              {secProgs.map((p, i) => {
                const live = p.live;
                const fresh = p.daysAgo == null
                  ? t('TrustCenterPage.testingNotRun', 'recently')
                  : (p.daysAgo === 0
                      ? t('TrustCenterPage.testingToday', 'today')
                      : t('TrustCenterPage.testingDaysAgo', '{{n}} days ago', { n: p.daysAgo }));
                const desc = {
                  asm:  t('TrustCenterPage.testingDescAsm',  'Our internet-facing assets are continuously discovered and monitored for new exposures.'),
                  iasm: t('TrustCenterPage.testingDescIasm', 'Our cloud and internal infrastructure is continuously mapped and assessed across every layer.'),
                  bas:  t('TrustCenterPage.testingDescBas',  'Real-world adversary techniques are safely simulated against our environment and mapped to MITRE ATT&CK.'),
                }[p.key];
                return (
                  <div key={i} className="tcp-st-card">
                    <div className="tcp-st-head">
                      <span className="tcp-st-name">{p.label}</span>
                      <span className={`tcp-st-badge ${live ? 'tcp-st-live' : 'tcp-st-stale'}`}>
                        <span className="tcp-st-dot" /> {t('TrustCenterPage.testingActive', 'Active')} · {t('TrustCenterPage.testingAssessed', 'assessed {{when}}', { when: fresh })}
                      </span>
                    </div>

                    {desc && <p className="tcp-st-desc">{desc}</p>}

                    {Array.isArray(p.layers) && p.layers.length > 0 && (
                      <div className="tcp-st-tags" aria-label="Coverage layers">
                        <span className="tcp-st-tags-label">{t('TrustCenterPage.testingCoverage', 'Coverage')}</span>
                        {p.layers.map((l, j) => <span key={j} className="tcp-st-tag">{l}</span>)}
                      </div>
                    )}

                    {p.assured && (
                      <div className="tcp-st-clean">
                        <i className="fas fa-check-circle" /> {t('TrustCenterPage.testingCleanCrit', 'All critical & high findings remediated')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="tcp-st-report">
              <span>{t('TrustCenterPage.testingReportNote', 'Need the full assessment, mapped to controls?')}</span>
              <button type="button" className="tcp-doc-btn" onClick={() => { setStReq({ reportType: 'security-testing' }); setStState('idle'); setStForm({ email: '', company: '' }); }}>
                <i className="fas fa-file-pdf" /> {t('TrustCenterPage.testingRequestReport', 'Request full report')}
              </button>
            </div>
          </div>
        )}

        {/* ── SUBPROCESSORS tab ── */}
        {tab === 'subprocessors' && hasSubs && (
          <div className="tcp-card">
            <h3 className="tcp-card-title">{t('TrustCenterPage.subprocessorsTitle')}</h3>
            <p className="tcp-subs-note">{t('TrustCenterPage.subprocessorsNote')}</p>
            <div className="tcp-subs-list">
              {data.subprocessors.map((s, i) => (
                <div key={i} className="tcp-sub-item">
                  <div className="tcp-sub-head">
                    <span className="tcp-sub-name">
                      {s.websiteUrl
                        ? <a href={s.websiteUrl} target="_blank" rel="noopener noreferrer">{s.name}</a>
                        : s.name}
                    </span>
                    {s.category && <span className="tcp-sub-cat">{s.category}</span>}
                  </div>
                  {s.region && (
                    <div className="tcp-sub-region">
                      <i className="fas fa-map-marker-alt" /> {s.region}
                    </div>
                  )}
                  {s.purpose && <p className="tcp-sub-purpose">{s.purpose}</p>}
                  {s.dataCategories && (
                    <p className="tcp-sub-data">
                      <strong>{t('TrustCenterPage.subprocessorsDataLabel')}</strong> {s.dataCategories}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── DOCUMENTS tab ── */}
        {tab === 'documents' && hasDocs && (
          <div className="tcp-card">
            <h3 className="tcp-card-title">{t('TrustCenterPage.documentsTitle')}</h3>
            <p className="tcp-subs-note">{t('TrustCenterPage.documentsNote')}</p>
            {Object.entries(docsByCategory).map(([cat, items]) => (
              <div key={cat} className="tcp-doc-group">
                <div className="tcp-doc-cat">{cat}</div>
                {items.map(d => (
                  <div key={d.id} className="tcp-doc-item">
                    <i className="fas fa-file-alt tcp-doc-icon" />
                    <span className="tcp-doc-title">{d.title}</span>
                    {d.gated ? (
                      <button type="button" className="tcp-doc-btn" onClick={() => openRequest(d)}>
                        <i className="fas fa-lock" /> {t('TrustCenterPage.documentsRequest')}
                      </button>
                    ) : (
                      // Ungated docs skip the request-access flow entirely: this hits the new
                      // ungated-only public endpoint (server-side fail-closed on gated/org/showOnTrust —
                      // see TrustDocumentService#publicDirectDownload), not the token-gated one.
                      <a
                        className="tcp-doc-btn"
                        href={`${axiosInstance.defaults.baseURL}/trust-center/public/${slug}/documents/${d.id}/download`}
                      >
                        <i className="fas fa-download" /> {t('TrustCenterPage.documentsDownload')}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="tcp-footer">
          {!data.hideCataamBranding && (
            <p className="tcp-footer-text">
              {t('TrustCenterPage.footerPoweredByPrefix')} <strong>CATAAM</strong> {t('TrustCenterPage.footerPoweredBySuffix')}
            </p>
          )}
          {data.lastUpdated && (
            <p className="tcp-footer-updated">
              {t('TrustCenterPage.lastUpdated', {
                date: new Date(data.lastUpdated).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric'
                })
              })}
            </p>
          )}
        </div>
      </div>

      {/* Security-report request modal */}
      {stReq && (
        <div className="tcp-modal-overlay" onClick={() => setStReq(null)}>
          <div className="tcp-modal" onClick={e => e.stopPropagation()}>
            {stState === 'done' ? (
              <div className="tcp-modal-done">
                <i className="fas fa-check-circle" />
                <h3>{t('TrustCenterPage.testingReqDoneTitle', 'Request submitted')}</h3>
                <p>{t('TrustCenterPage.testingReqDoneBody', "Your request has been submitted. You'll receive a download link by email once it's approved.")}</p>
                <button type="button" className="tcp-doc-btn" onClick={() => setStReq(null)}>
                  {t('TrustCenterPage.close')}
                </button>
              </div>
            ) : (
              <form onSubmit={submitReport}>
                <h3 className="tcp-card-title">{t('TrustCenterPage.testingRequestReport', 'Request full report')}</h3>
                <p className="tcp-subs-note">
                  {t('TrustCenterPage.testingReqNote', 'Enter your corporate email to request the full security-testing report (ASM, iASM & red-team). Once approved, you’ll be emailed a link to the live report.')}
                </p>
                <input type="email" required placeholder={t('TrustCenterPage.email')} value={stForm.email}
                       onChange={e => setStForm({ ...stForm, email: e.target.value })} />
                <input placeholder={t('TrustCenterPage.company')} value={stForm.company}
                       onChange={e => setStForm({ ...stForm, company: e.target.value })} />
                {stState === 'error' && <p className="tcp-modal-err">{t('TrustCenterPage.testingReqError', 'Please use a corporate email address and try again.')}</p>}
                <div className="tcp-modal-actions">
                  <button type="button" className="tcp-modal-cancel" onClick={() => setStReq(null)}>
                    {t('TrustCenterPage.cancel')}
                  </button>
                  <button type="submit" className="tcp-doc-btn" disabled={stState === 'sending'}>
                    {stState === 'sending' ? '…' : t('TrustCenterPage.testingRequestReport', 'Request full report')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Request-access modal */}
      {reqDoc && (
        <div className="tcp-modal-overlay" onClick={() => setReqDoc(null)}>
          <div className="tcp-modal" onClick={e => e.stopPropagation()}>
            {reqState === 'done' ? (
              <div className="tcp-modal-done">
                <i className="fas fa-check-circle" />
                <h3>{t('TrustCenterPage.requestDoneTitle')}</h3>
                <p>{t('TrustCenterPage.requestDoneBody')}</p>
                <button type="button" className="tcp-doc-btn" onClick={() => setReqDoc(null)}>
                  {t('TrustCenterPage.close')}
                </button>
              </div>
            ) : (
              <form onSubmit={submitRequest}>
                <h3 className="tcp-card-title">{t('TrustCenterPage.requestTitle')}</h3>
                <p className="tcp-subs-note">{reqDoc.title}</p>
                <div className="tcp-modal-row">
                  <input placeholder={t('TrustCenterPage.firstName')} value={reqForm.firstName}
                         onChange={e => setReqForm({ ...reqForm, firstName: e.target.value })} />
                  <input placeholder={t('TrustCenterPage.lastName')} value={reqForm.lastName}
                         onChange={e => setReqForm({ ...reqForm, lastName: e.target.value })} />
                </div>
                <input type="email" required placeholder={t('TrustCenterPage.email')} value={reqForm.email}
                       onChange={e => setReqForm({ ...reqForm, email: e.target.value })} />
                <input placeholder={t('TrustCenterPage.company')} value={reqForm.company}
                       onChange={e => setReqForm({ ...reqForm, company: e.target.value })} />
                <select value={reqForm.reason} onChange={e => setReqForm({ ...reqForm, reason: e.target.value })}>
                  <option value="">{t('TrustCenterPage.reasonSelect')}</option>
                  <option value="existing-customer">{t('TrustCenterPage.reasonExisting')}</option>
                  <option value="prospect">{t('TrustCenterPage.reasonProspect')}</option>
                  <option value="other">{t('TrustCenterPage.reasonOther')}</option>
                </select>
                {reqState === 'error' && <p className="tcp-modal-err">{t('TrustCenterPage.requestError')}</p>}
                <div className="tcp-modal-actions">
                  <button type="button" className="tcp-modal-cancel" onClick={() => setReqDoc(null)}>
                    {t('TrustCenterPage.cancel')}
                  </button>
                  <button type="submit" className="tcp-doc-btn" disabled={reqState === 'sending'}>
                    {reqState === 'sending' ? '…' : t('TrustCenterPage.requestSubmit')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TrustCenterPage;
